import { useState, useEffect } from 'react';
import { FiPhone, FiX, FiUser, FiBriefcase, FiMessageSquare, FiCheck } from 'react-icons/fi';
import { dialerAPI, lmsAPI } from '../services/api';
import { useNotification } from '../contexts/NotificationContext';

const OUTCOMES = [
  { value: 'interested',      label: 'Interested' },
  { value: 'callback',        label: 'Callback requested' },
  { value: 'not_interested',  label: 'Not interested' },
  { value: 'voicemail',       label: 'Left voicemail' },
  { value: 'no_answer',       label: 'No answer' },
  { value: 'other',           label: 'Other' },
];

function formatDuration(seconds) {
  if (!seconds) return '';
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return `${m}m ${s}s`;
}

export default function PostCallForm() {
  const { postCallTarget, clearPostCallTarget, registerCallback } = useNotification();

  const [name, setName] = useState('');
  const [company, setCompany] = useState('');
  const [outcome, setOutcome] = useState('');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [recordingUrl, setRecordingUrl] = useState(null);
  const [callDuration, setCallDuration] = useState(null);

  // Pre-fill if we already know the lead
  useEffect(() => {
    if (!postCallTarget) return;
    setName('');
    setCompany('');
    setOutcome('');
    setNotes('');
    setSaved(false);
    setRecordingUrl(null);
    setCallDuration(postCallTarget.duration || null);
  }, [postCallTarget]);

  // Listen for recording becoming available after upload
  useEffect(() => {
    const unsub = registerCallback('dialer:recording_ready', ({ callLogId }) => {
      if (postCallTarget && String(callLogId) === String(postCallTarget.callLogId)) {
        setRecordingUrl(dialerAPI.getRecordingUrl(callLogId));
      }
    });
    return unsub;
  }, [postCallTarget, registerCallback]);

  if (!postCallTarget) return null;

  const isKnownLead = !!postCallTarget.leadId;

  async function handleSave() {
    setSaving(true);
    try {
      // If this is a new caller, create/link a lead first
      if (!isKnownLead && (name || company)) {
        await dialerAPI.linkLead({
          callLogId: postCallTarget.callLogId,
          newLead: {
            title: name || company || postCallTarget.phoneNumber,
            company,
            contacts: [{
              name,
              phone: postCallTarget.phoneNumber,
              isPrimary: true,
            }],
          },
        });
      }

      // Update the auto-created activity with outcome + notes
      if (postCallTarget.activityId && (outcome || notes)) {
        await lmsAPI.editActivity(postCallTarget.activityId, {
          outcome,
          summary: notes || undefined,
        });
      }

      setSaved(true);
      setTimeout(clearPostCallTarget, 1200);
    } catch (err) {
      console.error('[PostCallForm] save error:', err);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="modal-overlay" style={{ zIndex: 9999 }} onClick={clearPostCallTarget}>
      <div
        className="lms-modal"
        style={{ width: 420, maxWidth: '95vw' }}
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className="lms-modal-header" style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <FiPhone size={16} style={{ color: 'var(--accent, #6366f1)' }} />
          <span style={{ fontWeight: 600, fontSize: 15, flex: 1 }}>
            Log work call
          </span>
          <span style={{ fontSize: 12, color: '#888' }}>
            {postCallTarget.contactName || postCallTarget.phoneNumber}
            {callDuration ? ` · ${formatDuration(callDuration)}` : ''}
          </span>
          <button className="lms-icon-btn" onClick={clearPostCallTarget}>
            <FiX size={15} />
          </button>
        </div>

        <div style={{ padding: '16px 20px', display: 'flex', flexDirection: 'column', gap: 12 }}>
          {/* Recording player — appears once upload completes */}
          {recordingUrl && (
            <div>
              <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
                Recording
              </label>
              <audio
                controls
                src={recordingUrl}
                style={{ width: '100%', height: 36 }}
              />
            </div>
          )}

          {/* Name + company — only for unknown callers */}
          {!isKnownLead && (
            <>
              <div>
                <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
                  <FiUser size={11} style={{ marginRight: 4 }} />Contact name
                </label>
                <input
                  className="lms-input"
                  style={{ width: '100%' }}
                  placeholder="e.g. Rajesh Kumar"
                  value={name}
                  onChange={e => setName(e.target.value)}
                />
              </div>
              <div>
                <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
                  <FiBriefcase size={11} style={{ marginRight: 4 }} />Company
                </label>
                <input
                  className="lms-input"
                  style={{ width: '100%' }}
                  placeholder="e.g. Infosys Ltd"
                  value={company}
                  onChange={e => setCompany(e.target.value)}
                />
              </div>
            </>
          )}

          {/* Outcome */}
          <div>
            <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
              Outcome
            </label>
            <select
              className="lms-input"
              style={{ width: '100%' }}
              value={outcome}
              onChange={e => setOutcome(e.target.value)}
            >
              <option value="">Select outcome…</option>
              {OUTCOMES.map(o => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          {/* Notes */}
          <div>
            <label style={{ fontSize: 11, color: '#888', display: 'block', marginBottom: 4 }}>
              <FiMessageSquare size={11} style={{ marginRight: 4 }} />Notes
            </label>
            <textarea
              className="lms-input"
              style={{ width: '100%', minHeight: 72, resize: 'vertical' }}
              placeholder="What was discussed?"
              value={notes}
              onChange={e => setNotes(e.target.value)}
            />
          </div>

          {/* Actions */}
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: 8, marginTop: 4 }}>
            <button className="lms-btn" onClick={clearPostCallTarget}>
              Skip
            </button>
            <button
              className="lms-btn primary"
              onClick={handleSave}
              disabled={saving || saved}
            >
              {saved
                ? <><FiCheck size={13} /> Saved</>
                : saving
                  ? 'Saving…'
                  : 'Save to CRM'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
